import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { ArrowRightLeft, PackageOpen } from 'lucide-react';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { Skeleton } from '@/components/ui/skeleton';
import { useLocation } from '@/contexts/LocationContext';
import { DATE_FORMAT, useDateFnsLocale } from '@/lib/format-dates';
import { trpc } from '@/lib/trpc';

interface IncomingHoverCardProps {
  /** SKU uuid — the `id` field on the inventory Product (same id other inventory queries use). */
  skuId: string;
  /** The incoming quantity already shown in the cell (per-location or aggregated). */
  incoming: number;
}

/**
 * BLU-579 — hovering the "incoming" quantity reveals the purchase / transfer
 * orders that quantity is coming in on. Rows are fetched lazily (only when the
 * HoverCard opens) and reconcile with the incoming number because the backend
 * uses the same predicate as the inventory view.
 *
 * Location scoping: reads `selectedLocation` from LocationContext and passes it
 * as `locationId` when a location is chosen; in "All Locations" it omits the
 * filter so the breakdown lists every contributing order (the sum still
 * reconciles with the aggregated incoming number).
 *
 * When `incoming <= 0` the caller renders a plain number — no HoverCard.
 */
export function IncomingHoverCard({ skuId, incoming }: IncomingHoverCardProps) {
  const { t } = useTranslation('inventory');
  const { selectedLocation } = useLocation();
  const dateLocale = useDateFnsLocale();
  const [open, setOpen] = useState(false);

  const query = trpc.inventory.getIncomingBreakdown.useQuery(
    { skuId, locationId: selectedLocation?.id },
    {
      enabled: open,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  );

  const rows = query.data ?? [];

  const formatEta = (etaDate: string | null) => {
    if (!etaDate) return t('incomingBreakdown.noEta');
    try {
      return t('incomingBreakdown.eta', {
        date: format(parseISO(etaDate), DATE_FORMAT, { locale: dateLocale }),
      });
    } catch {
      return t('incomingBreakdown.noEta');
    }
  };

  return (
    <HoverCard open={open} onOpenChange={setOpen} openDelay={150} closeDelay={100}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setOpen(true);
          }}
          onKeyDown={(event) => {
            // Keep the row's Enter/Space handler from firing (opens the detail modal).
            if (event.key === 'Enter' || event.key === ' ') {
              event.stopPropagation();
            }
          }}
          className="inline-flex items-center tabular-nums cursor-help rounded-sm underline decoration-dotted decoration-muted-foreground/50 underline-offset-4 transition-colors hover:decoration-threadheads-purple focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 motion-reduce:transition-none"
          aria-label={t('incomingBreakdown.ariaLabel', { count: incoming })}
        >
          {incoming}
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        align="end"
        side="bottom"
        className="w-72 p-0"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="border-b px-3 py-2">
          <p className="text-xs font-medium text-foreground">
            {t('incomingBreakdown.title')}
          </p>
        </div>

        {query.isLoading ? (
          <div className="space-y-2 px-3 py-2" aria-hidden="true">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={`incoming-skel-${i}`} className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1 space-y-1.5">
                  <Skeleton className="h-3.5 w-24" />
                  <Skeleton className="h-3 w-20" />
                </div>
                <Skeleton className="h-3.5 w-8" />
              </div>
            ))}
          </div>
        ) : query.isError ? (
          <p className="px-3 py-3 text-xs text-destructive" role="status">
            {t('incomingBreakdown.error')}
          </p>
        ) : rows.length === 0 ? (
          <p className="px-3 py-3 text-xs text-muted-foreground" role="status">
            {t('incomingBreakdown.empty')}
          </p>
        ) : (
          <ul className="max-h-64 overflow-y-auto py-1">
            {rows.map((row) => {
              const isTransfer = row.orderType === 'transfer';
              const TypeIcon = isTransfer ? ArrowRightLeft : PackageOpen;
              const typeLabel = isTransfer
                ? t('incomingBreakdown.transfer')
                : t('incomingBreakdown.purchase');
              return (
                <li
                  key={row.poId}
                  className="flex items-start justify-between gap-3 px-3 py-2 hover:bg-muted/40 motion-reduce:hover:bg-muted/40"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <TypeIcon
                        className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                        aria-hidden="true"
                      />
                      <Link
                        to={`/orders/${row.poId}`}
                        onClick={(event) => event.stopPropagation()}
                        className="truncate font-mono text-xs text-threadheads-purple hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded-sm"
                      >
                        {row.poNumber ?? t('incomingBreakdown.noPoNumber')}
                      </Link>
                    </div>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">
                      {typeLabel}
                      {' · '}
                      {formatEta(row.etaDate)}
                    </p>
                  </div>
                  <span className="shrink-0 text-xs font-medium tabular-nums text-foreground">
                    {t('incomingBreakdown.units', { count: row.outstanding })}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </HoverCardContent>
    </HoverCard>
  );
}
